import axios from "axios";
import {
  FETCH_CONVERSATIONS,
  FETCH_CONVERSATION,
  CREATE_CONVERSATION,
  UPDATE_CONVERSATION,
  DELETE_CONVERSATION,
  FETCH_MESSAGES,
  SEND_MESSAGE,
  DELETE_MESSAGE,
  ADD_PARTICIPANT,
  REMOVE_PARTICIPANT,
  FETCH_UNREAD_COUNT,
  UPDATE_UNREAD_COUNT,
  MARK_AS_READ,
  UPLOAD_MESSAGE_ATTACHMENT,
  DELETE_MESSAGE_ATTACHMENT,
  MESSAGING_ERROR,
  MESSAGING_LOADING,
  START_LOADING,
  END_LOADING,
  WEBSOCKET_SEND_TYPING,
  WEBSOCKET_SEND_MESSAGE,
  UPDATE_MESSAGE,
  WEBSOCKET_JOIN_CONVERSATION,
  WEBSOCKET_LEAVE_CONVERSATION,
} from "./constants";

import * as api from "../api/index.js";

// Helper function to handle errors
const handleError = (error, dispatch) => {
  console.error("Error:", error);
  dispatch({
    type: MESSAGING_ERROR,
    payload: error.response?.data?.message || "An error occurred",
  });
  dispatch({ type: MESSAGING_LOADING });
};

// Action Creators

// Fetch all conversations for the current user
export const fetchConversations = () => async (dispatch) => {
  try {
    dispatch({ type: MESSAGING_LOADING });
    const { data } = await api.fetchConversations();

    dispatch({ type: FETCH_CONVERSATIONS, payload: data });
    
    // Join all conversations to receive real-time updates
    // Only join if there are conversations and they are in the expected format
    if (data.data?.conversations && Array.isArray(data.data.conversations)) {
      // Use a Set to track unique conversation IDs
      const uniqueConversationIds = new Set();
      
      data.data.conversations.forEach(conversation => {
        if (conversation._id && !uniqueConversationIds.has(conversation._id)) {
          uniqueConversationIds.add(conversation._id);
          dispatch({
            type: WEBSOCKET_JOIN_CONVERSATION,
            payload: { conversationId: conversation._id }
          });
        }
      });
    }
  } catch (error) {
    handleError(error, dispatch);
  }
};

// Fetch a single conversation by ID
export const fetchConversation = (id) => async (dispatch) => {
  try {
    if (!id) {
      console.error("Cannot fetch conversation: No ID provided");
      return;
    }
    
    dispatch({ type: MESSAGING_LOADING });
    const { data } = await api.fetchConversation(id);

    dispatch({ type: FETCH_CONVERSATION, payload: data });
    
    // Join the conversation to receive real-time updates
    dispatch({
      type: WEBSOCKET_JOIN_CONVERSATION,
      payload: { conversationId: id }
    });
  } catch (error) {
    handleError(error, dispatch);
  }
};

// Create a new conversation
export const createConversation = (conversationData) => async (dispatch) => {
  try {
    dispatch({ type: START_LOADING });
    
    const { data } = await api.createConversation(conversationData);
    
    // Handle response structure: { success: true, data: {...} }
    const conversation = data.data || data;
    
    if (!conversation || !conversation._id) {
      console.error("Failed to create conversation: Invalid response data");
      dispatch({ type: END_LOADING });
      return null;
    }
    
    dispatch({ type: CREATE_CONVERSATION, payload: conversation });
    
    // Join the new conversation to receive real-time updates
    dispatch({
      type: WEBSOCKET_JOIN_CONVERSATION,
      payload: { conversationId: conversation._id }
    });
    
    // No need to fetch all conversations again, just return the new one
    dispatch({ type: END_LOADING });
    return conversation;
  } catch (error) {
    handleError(error, dispatch);
    return null;
  }
};

// Update an existing conversation
export const updateConversation = (id, updateData) => async (dispatch) => {
  try {
    dispatch({ type: START_LOADING });
    const { data } = await api.updateConversation(id, updateData);
    
    dispatch({ type: UPDATE_CONVERSATION, payload: { id, data } });
    dispatch({ type: END_LOADING });
  } catch (error) {
    handleError(error, dispatch);
  }
};

// Delete a conversation
export const deleteConversation = (conversationId) => async (dispatch) => {
  try {
    dispatch({ type: START_LOADING });
    
    // Leave the conversation to stop receiving updates
    dispatch({
      type: WEBSOCKET_LEAVE_CONVERSATION,
      payload: { conversationId }
    });
    
    await api.deleteConversation(conversationId);
    
    dispatch({
      type: DELETE_CONVERSATION,
      payload: { conversationId }
    });
    
    // After deleting, fetch the updated list
    dispatch(fetchConversations());
  } catch (error) {
    handleError(error, dispatch);
  }
};

// Fetch messages for a conversation
export const fetchMessages = (conversationId, page = 1, limit = 50) => async (dispatch) => {
  try {
    if (!conversationId) {
      console.error("Cannot fetch messages: No conversation ID provided");
      return;
    }
    
    dispatch({ type: START_LOADING });
    
    const { data } = await api.fetchMessages(conversationId, page, limit);
    
    dispatch({
      type: FETCH_MESSAGES,
      payload: {
        conversationId,
        messages: data.data,
        pagination: data.pagination
      }
    });
    
    dispatch({ type: END_LOADING });
  } catch (error) {
    handleError(error, dispatch);
  }
};

// Send a new message in a conversation
export const sendMessage = (conversationId, messageData) => async (dispatch) => {
  try {
    if (!conversationId) {
      console.error("Cannot send message: No conversation ID provided");
      return null;
    }
    
    // Generate a temporary ID for the message
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Create a temporary message for immediate UI feedback
    const currentUser = JSON.parse(localStorage.getItem('profile'))?.result;
    const tempMessage = {
      _id: tempId,
      sender: {
        _id: currentUser?._id,
        name: currentUser?.name,
        avatar: currentUser?.avatar
      },
      content: messageData.content,
      attachments: messageData.attachments || [],
      createdAt: new Date().toISOString(),
      status: 'sending',
      tempId
    };
    
    // Dispatch temporary message for immediate UI update
    dispatch({
      type: SEND_MESSAGE,
      payload: {
        conversationId,
        message: tempMessage,
        isTemporary: true
      }
    });
    
    // Send message via WebSocket for real-time delivery
    if (messageData.sendViaWebSocket !== false) {
      dispatch({
        type: WEBSOCKET_SEND_MESSAGE,
        payload: {
          ...messageData,
          conversationId,
          tempId
        }
      });
    }
    
    // Also send via API for persistence
    const { data } = await api.sendMessage(conversationId, messageData);
    
    // Replace temporary message with the real one from the server
    dispatch({
      type: SEND_MESSAGE,
      payload: {
        conversationId,
        message: {
          ...data,
          tempId // Include tempId for reference
        },
        isRealTime: true
      }
    });
    
    return data;
  } catch (error) {
    // Update the temporary message to show error status
    dispatch({
      type: UPDATE_MESSAGE,
      payload: {
        conversationId,
        messageId: `temp-${Date.now()}`,
        message: {
          status: 'error'
        }
      }
    });
    
    handleError(error, dispatch);
    return null;
  }
};

// Delete a message
export const deleteMessage = (conversationId, messageId) => async (dispatch) => {
  try {
    dispatch({ type: START_LOADING });
    
    await api.deleteMessage(conversationId, messageId);
    
    dispatch({
      type: DELETE_MESSAGE,
      payload: { conversationId, messageId }
    });
    
    dispatch({ type: END_LOADING });
  } catch (error) {
    handleError(error, dispatch);
  }
};

// Add a participant to a conversation
export const addParticipant = (conversationId, userData) => async (dispatch) => {
  try {
    dispatch({ type: START_LOADING });
    
    const { data } = await api.addParticipant(conversationId, userData);
    
    dispatch({
      type: ADD_PARTICIPANT,
      payload: { conversationId, participant: data }
    });
    
    dispatch({ type: END_LOADING });
  } catch (error) {
    handleError(error, dispatch);
  }
};

// Remove a participant from a conversation
export const removeParticipant = (conversationId, userId) => async (dispatch) => {
  try {
    dispatch({ type: START_LOADING });
    
    await api.removeParticipant(conversationId, userId);
    
    dispatch({
      type: REMOVE_PARTICIPANT,
      payload: { conversationId, userId }
    });
    
    dispatch({ type: END_LOADING });
  } catch (error) {
    handleError(error, dispatch);
  }
};

// Fetch unread message count
export const fetchUnreadCount = () => async (dispatch) => {
  try {
    dispatch({ type: START_LOADING });
    
    const { data } = await api.fetchUnreadCount();
    
    dispatch({
      type: FETCH_UNREAD_COUNT,
      payload: data
    });
    
    dispatch({ type: END_LOADING });
  } catch (error) {
    handleError(error, dispatch);
  }
};

// Fetch users for messaging
export const fetchMessagingUsers = (searchQuery = "", page = 1, limit = 20) => async (dispatch) => {
  try {
    const { data } = await api.fetchMessagingUsers(searchQuery, page, limit);
    return data;
  } catch (error) {
    console.error("Error fetching messaging users:", error);
    return { data: [], pagination: { total: 0 } };
  }
};

// Mark conversation as read
export const markConversationAsRead = (conversationId) => async (dispatch) => {
  try {
    if (!conversationId) {
      console.error("Cannot mark as read: No conversation ID provided");
      return;
    }
    
    dispatch({ type: START_LOADING });
    
    // Optimistically update UI
    dispatch({
      type: MARK_AS_READ,
      payload: { conversationId }
    });
    
    // Reset unread count for this conversation
    dispatch({
      type: UPDATE_UNREAD_COUNT,
      payload: {
        conversationId,
        increment: false,
        reset: true
      }
    });
    
    // Send read receipt via API for persistence
    // This will avoid the websocket loop as the server will handle broadcasting
    await api.markAsRead(conversationId);
    
    dispatch({ type: END_LOADING });
  } catch (error) {
    handleError(error, dispatch);
  }
};

// Send typing indicator via Socket.io
export const sendTypingIndicator = (conversationId, isTyping) => (dispatch) => {
  dispatch({
    type: WEBSOCKET_SEND_TYPING,
    payload: { conversationId, isTyping }
  });
};

// Upload message attachment
export const uploadMessageAttachment = (conversationId, formData) => async (dispatch) => {
  try {
    dispatch({ type: START_LOADING });
    
    const { data } = await api.uploadMessageAttachment(conversationId, formData);
    
    dispatch({
      type: UPLOAD_MESSAGE_ATTACHMENT,
      payload: {
        conversationId,
        attachment: data
      }
    });
    
    dispatch({ type: END_LOADING });
    return data;
  } catch (error) {
    handleError(error, dispatch);
    return null;
  }
};

// Delete message attachment
export const deleteMessageAttachment = (conversationId, attachmentId) => async (dispatch) => {
  try {
    dispatch({ type: START_LOADING });
    
    await api.deleteMessageAttachment(conversationId, attachmentId);
    
    dispatch({
      type: DELETE_MESSAGE_ATTACHMENT,
      payload: {
        conversationId,
        attachmentId
      }
    });
    
    dispatch({ type: END_LOADING });
  } catch (error) {
    handleError(error, dispatch);
  }
};
